import { graphql } from 'gql.tada'
import { useQuery } from '@urql/vue'

import { medication as medicationQueryString } from '~/queries/strings/medication.js'
import { medications as medicationsQueryString } from '~/queries/strings/medications.js'
import { activeIngredient as activeIngredientQueryString } from '~/queries/strings/activeIngredient.js'
import { activeIngredients as activeIngredientsQueryString } from '~/queries/strings/activeIngredients.js'
import { generalInformation as generalInformationQueryString } from '~/queries/strings/generalInformation.js'
import { terms as termsQueryString } from '~/queries/strings/legend.js'
import { assetFragment as assetFragmentQueryString } from '~/queries/strings/assetFragment'
import { manual as manualQueryString } from '~/queries/strings/manual.js'

const chunkSize = 10

export const usePrefetch = async () => {
  const { $urql, $i18n } = useNuxtApp()
  const site = ref($i18n.locale.value)

  const collectionResults = await $urql.query(
    graphql(`
    query CollectionResults($site: String!) {
      ${medicationsQueryString}
      ${activeIngredientsQueryString}
    }
  `),
    { site: site.value },
  )

  const completeEntries: Record<string, any> = {}

  const getPartialData = async (query: string) => {
    return await $urql.query(
      graphql(`
        query PartialData($site: String!) {
          ${query}
        }
      `),
      { site: site.value },
    )
  }

  const medications = collectionResults.data?.medications?.data ?? []

  for (let i = 0; i < medications.length; i += chunkSize) {
    let medicationsQuery = ''
    medications.slice(i, i + chunkSize).forEach((element) => {
      medicationsQuery += `
      med_${element?.slug?.replace(/-/g, '_')}: entry(slug: "${element?.slug}", site: $site) {
        ${medicationQueryString}
      }`
    })
    const partialData = await getPartialData(medicationsQuery)
    Object.entries(partialData.data ?? {}).forEach(([key, value]) => {
      completeEntries[key] = value
    })
  }

  const activeIngredients =
    collectionResults.data?.activeIngredients?.data ?? []

  for (let i = 0; i < activeIngredients.length; i += chunkSize) {
    let activeIngredientsQuery = ''
    activeIngredients.slice(i, i + chunkSize).forEach((element) => {
      activeIngredientsQuery += `
      aing_${element?.slug?.replace(/-/g, '_')}: entry(slug: "${element?.slug}", site: $site) {
        ${activeIngredientQueryString}
      }`
    })
    const partialData = await getPartialData(activeIngredientsQuery)
    Object.entries(partialData.data ?? {}).forEach(([key, value]) => {
      completeEntries[key] = value
    })
  }

  const remainingData = await $urql.query(
    graphql(`
    query all($site: String!) {
      ${medicationsQueryString}
      ${activeIngredientsQueryString}
      ${generalInformationQueryString}
      ${termsQueryString}
      ${manualQueryString}
    }
    ${assetFragmentQueryString}
  `),
    { site: site.value },
  )

  Object.entries(remainingData.data ?? {}).forEach(([key, value]) => {
    completeEntries[key] = value
  })

  return completeEntries
}
